// Pure view layer for the Shot Map module — ported from js/renderers-shotmap.js (the Shot
// Map section: colours, the goal-mouth placement chart, Shot Details, the stats bar, the
// table, the freeze-frame reveal, and the click-to-lock / magnetic-hover hit rules). DOM-
// free and unit-tested; ShotMapModule (04l-d) paints what these return as JSX.

import type { ProtoEvent } from "@catbento/shared";
import {
  clamp,
  normalize,
  trueScaleToPitch,
  lerpEventColor,
  EVENT_BODY_PART_COLORS,
  EVENT_COLOR_NEUTRAL,
  formatMatchDate,
  oppositionLabel,
  sbAttrs,
  shotOf,
  enrichedOf,
  type EventColorMode,
  type EventContext,
} from "../engines/event-map-kit.js";
import { divergingBarRows, eventMatchRows, eventTimeLabel, type DivergingRow } from "./event-details-view.js";
import { clockMinute } from "../data/match-minute.js";
import { getPlayerShortName } from "../data/player-utils.js";

// --- Colour ------------------------------------------------------------------------------

/** Outcome colour: goal green, on-target blue, everything else grey. */
export function shotColor(shot: ProtoEvent): string {
  if (shotOf(shot).outcome.name === "Goal") return "#2ecc71";
  if (enrichedOf(shot)?.on_target) return "#4d8bf5";
  return "#888";
}

export function shotOutcomePillClass(shot: ProtoEvent): string {
  if (shotOf(shot).outcome.name === "Goal") return "event-pill-good";
  if (enrichedOf(shot)?.on_target) return "event-pill-neutral";
  return "event-pill-bad";
}

export const SHOT_MARKER_RADIUS = 0.6;

export const TECHNIQUE_COLORS: Readonly<Record<string, string>> = {
  Normal: EVENT_COLOR_NEUTRAL,
  "Half Volley": "rgb(77, 139, 245)",
  Volley: "rgb(245, 166, 35)",
  "Diving Header": "rgb(176, 109, 242)",
  Lob: "rgb(46, 204, 113)",
  Backheel: "rgb(226, 88, 107)",
};

export const SHOT_COLOR_MODES: readonly EventColorMode[] = [
  { key: "outcome", label: "Outcome", getColor: (s) => shotColor(s) },
  {
    key: "xg",
    label: "xG",
    // Saturates at 0.3 xG. `|| 0` verbatim (a missing xG paints/sorts as 0).
    getColor: (s) => lerpEventColor((shotOf(s).statsbomb_xg || 0) / 0.3),
    sortValue: (s) => shotOf(s).statsbomb_xg || 0,
  },
  { key: "bodyPart", label: "Body Part", getColor: (s) => EVENT_BODY_PART_COLORS[shotOf(s).body_part.name] || EVENT_COLOR_NEUTRAL },
  {
    key: "technique",
    label: "Technique",
    getColor: (s) => TECHNIQUE_COLORS[shotOf(s).technique ? shotOf(s).technique!.name : ""] || EVENT_COLOR_NEUTRAL,
  },
];

/** Only a shot that tested the keeper (on target, or a goal) gets the colour mode's hue;
 *  every other outcome is a plain dark red — it never threatened. The same colour feeds the
 *  marker AND its trajectory gradient (they once disagreed). */
export function shotMarkerColor(shot: ProtoEvent, mode: EventColorMode): string {
  const filled = enrichedOf(shot)?.on_target || shotOf(shot).outcome.name === "Goal";
  return filled ? mode.getColor(shot) : "#8b0000";
}

// --- Marker geometry + tooltip ---------------------------------------------------------------

/** The shot's origin (plotted space) and its trajectory end — end_location is in the TRUE
 *  120×80 space, so it's rescaled; no end_location → a zero-length line at the origin. */
export function shotGeometry(shot: ProtoEvent): { x: number; y: number; ex: number; ey: number } {
  const [x, y] = normalize(shot.x, shot.y);
  const end = shotOf(shot).end_location;
  const [ex, ey] = end ? trueScaleToPitch(end[0]!, end[1]!) : [x, y];
  return { x, y, ex, ey };
}

export function shotTooltip(ctx: EventContext, shot: ProtoEvent): string {
  const info = shotOf(shot);
  // A team's map: who took it first (Catalyst names the shooter too).
  const who = shot.player ? `${getPlayerShortName({ name: shot.player.name })}\n` : "";
  return `${who}${oppositionLabel(ctx, shot)}\n${info.body_part.name} · ${clockMinute(shot.minute, shot.period)}\n${info.outcome.name} · xG ${(info.statsbomb_xg || 0).toFixed(2)}`;
}

// --- Freeze frame ------------------------------------------------------------------------------

export interface FreezeFramePlayer {
  location: number[];
  teammate: boolean;
  player: { name: string; id?: number };
  position?: { name: string } | null;
  /** Some frames mark the shooter herself (StatsBomb 360's `actor`). */
  actor?: boolean;
}

/** Everyone else on the pitch at the moment of the shot. The shooter is left out (Ben, 2026-10-19:
 *  Haaland was drawn as a "teammate" goal-side of his own shot, as if blocking it): some frames
 *  include her, and her shot's own marker already shows where she was. */
export function freezeFrameOf(shot: ProtoEvent): FreezeFramePlayer[] {
  const frame = (shotOf(shot).freeze_frame as FreezeFramePlayer[] | undefined) || [];
  const shooter = (shot.raw_event_attributes as { player?: { id?: number; name?: string } }).player;
  return frame.filter(
    (f) =>
      !f.actor &&
      !(shooter?.id !== undefined && f.player.id === shooter.id) &&
      !(shooter?.name !== undefined && f.player.name === shooter.name),
  );
}

export interface FreezeFrameDot {
  x: number;
  y: number;
  /** Degrees, 1dp string (as the legacy transform). */
  angle: string;
  className: string;
  tooltip: string;
}

/** Every other player's position at the moment of the shot, drawn as a top-down player
 *  shape turned to face the ball: the shape's default facing is atan2's 90°, so rotate by
 *  (angle to ball − 90). Goalkeepers get their own class (a yellow kit on either side). */
export function freezeFrameDots(origin: { x: number; y: number }, freeze: FreezeFramePlayer[]): FreezeFrameDot[] {
  return freeze.map((f) => {
    const [fx, fy] = trueScaleToPitch(f.location[0]!, f.location[1]!);
    const angle = (Math.atan2(origin.y - fy, origin.x - fx) * 180) / Math.PI - 90;
    const role = f.teammate ? "Teammate" : "Opponent";
    const posName = f.position ? f.position.name : "";
    const isGk = posName === "Goalkeeper";
    return {
      x: fx,
      y: fy,
      angle: angle.toFixed(1),
      className: `freeze-frame-dot ${f.teammate ? "teammate" : "opponent"}${isGk ? " goalkeeper" : ""}`,
      tooltip: `${f.player.name}\n${posName ? posName + " · " : ""}${role}`,
    };
  });
}

// --- Click-to-lock + magnetic hover hit rules ----------------------------------------------------
// Distance is to each marker's EDGE (centre distance − its radius), matching what's drawn.
// A click locks within SHOT_CLICK_PAD; the resting hover acquires within the same pad, then
// holds within SHOT_HOVER_STICKY_PAD unless another marker is meaningfully closer (SWITCH_MARGIN)
// — hollow markers can't be hit-tested natively, so this is magnetic by design.

export const SHOT_CLICK_PAD = 1.2;
export const SHOT_HOVER_STICKY_PAD = 2.5;
export const SHOT_HOVER_SWITCH_MARGIN = 0.6;

export interface ShotHitPoint {
  x: number;
  y: number;
  radius: number;
}

export function padDist(p: { x: number; y: number }, pd: ShotHitPoint): number {
  return Math.max(0, Math.hypot(p.x - pd.x, p.y - pd.y) - pd.radius);
}

function nearest<T extends ShotHitPoint>(p: { x: number; y: number }, points: T[]): { best: T | null; bestDist: number } {
  let best: T | null = null;
  let bestDist = Infinity;
  for (const pd of points) {
    const d = padDist(p, pd);
    if (d < bestDist) {
      bestDist = d;
      best = pd;
    }
  }
  return { best, bestDist };
}

/** The shot a click at p hits, or null. */
export function shotAtClick<T extends ShotHitPoint>(p: { x: number; y: number }, points: T[]): T | null {
  const { best, bestDist } = nearest(p, points);
  return best && bestDist <= SHOT_CLICK_PAD ? best : null;
}

/** The next hovered shot given the current one (sticky, with a switch margin). */
export function nextHoveredShot<T extends ShotHitPoint>(p: { x: number; y: number }, points: T[], hovered: T | null): T | null {
  const { best, bestDist } = nearest(p, points);
  if (hovered) {
    const curDist = padDist(p, hovered);
    const stolenByCloser = best !== hovered && bestDist < curDist * SHOT_HOVER_SWITCH_MARGIN;
    if (curDist <= SHOT_HOVER_STICKY_PAD && !stolenByCloser) return hovered;
  }
  return best && bestDist <= SHOT_CLICK_PAD ? best : null;
}

// --- Shot placement (goal-mouth) chart --------------------------------------------------------
// end_location plotted against the real goal frame. Anchored on the TRUE StatsBomb goal
// centre (y=40, not 74/2 — end_location is true 120×80). The domains keep the same yards-
// per-unit on both axes (12 yds wide × 4 tall in a 120×40 viewBox) so the goal draws at its
// true ~3:1 shape. Width is clamped to ±6 yds; height only at the floor — a skied shot
// genuinely paints above the chart, onto its label (overflow:visible, by request).

export const GOAL_CENTER_Y = 40;
export const GOAL_HALF_WIDTH = 4;
export const GOAL_HEIGHT = 2.67;
export const GOAL_CHART_Y_RANGE = 6;
export const GOAL_CHART_Z_RANGE = 4;
export const GOAL_CHART_VB_W = 120;
export const GOAL_CHART_VB_H = 40;
/** A plausible keeper height (still under the crossbar) + the silhouette's own aspect. */
export const GOALKEEPER_HEIGHT_YARDS = 2.4;
export const GOALKEEPER_SVG_ASPECT = 564.7 / 1082.2;

/** The supplied goalkeeper silhouette (viewBox 0 0 564.7 1082.2), path data verbatim. */
export const GOALKEEPER_SVG_PATHS: readonly string[] = [
  "M388.7,759.7c-17.3,3.5-33.6,4.8-50.9,4c21.6-2.9,43.3-5.2,64-12.7c11.7-9.7-2.8-34.4-1.8-48.9 c-12.6-57.3-15.1-126.7-38.3-177.7c-83.4,50.4-162.1-34.8-173.1,38.5c-9.6,58.1-22.8,120.4-29.9,180.5 c6.2,17.8,40.4,14.4,57.2,18.9c23.2,3.5-30.4,1.5-40.7-3c6.6,21.3-8.5,50.4,4.9,67.1c41.8,24.8,63.6-15.9,65.9-53.4 c1-7.9,4.6-11.4,12.3-12.3c21.7-2.5,12.4-85.6,26.5-68.1c8.3,30.2,2.1,62.5,32.2,75.6c4.8,20.3,7.1,46.1,22.2,61.2 C399.3,849.1,388.9,800.2,388.7,759.7z",
  "M398.8,1005.2c-3.6-11.5,4.6-33.5-11.9-33.4c30.2-9.3,11.2-60.7,18.9-86.9c3.2-22.1-16.9-0.8-29.2-3.8 c34.4-5.8,23.2-33.4,16.7-58.6c-17.5,18.3-38,12.9-58.9,9.3c0,62.5,25.7,119.4,27.6,181.6C368.4,1033.1,382.3,994.3,398.8,1005.2z",
  "M170.6,822.6c-5,27.2-17.1,53.8,19.3,58c-10.9,1.2-20.3-2.9-30.2-7.7c-0.8,27.1,1.8,54.1,3,81.2 c0.3,16.7,3.4,13.9,17.3,18.3c-19.3-3.3-10.5,20.2-14.1,32.8c17.6-6.1,20.8,11.2,35.4,16.3c3.4-64.9,27.1-124.7,28.2-189.4 C209,835.8,188.6,840.5,170.6,822.6z",
  "M204.8,1017.9c-7,13.7-19.6-5-25.1-9.8c-19.1-7.6-26.1,17.1-36.6,27.9c-10.2,11.6-37.9,25.7-18.7,42.1 c14-4.3,24.8,6.9,30.8,1c17.5-2.3,11.6-2.5,26-15.4c5.2-5.3,11.2,8.3,15.2-0.6c1.8-6.8,8.2,2.7,10.7-4.3 C212.8,1049.9,212.4,1011.7,204.8,1017.9z",
  "M437.3,1050.7c-19.1-7-39.1-68.1-57.9-33.7c-8.3,1.8-16.5,12.1-20.4-0.4c-6.7-1.2-7.1,29.9-3.1,39.2 c2.4,6.8,3.6,7.3,10.4,4.4c4.2,10.9,7.3,1.8,15.3,3c10.3,0.7,11.5,19.3,21.4,14.9c5.6-4.7,7.9,8.2,13.5,3 c6.1-5.4,12.7-4.6,19.6-2.7C450.9,1074.9,448.9,1058.1,437.3,1050.7z",
  "M564.4,27.6c-4.9-10.8-12.4,5.8-16.5,7.3c4.5-6.1,19.7-23.6,3.2-23.6c-3.9,5.2-5.8,7.9-6.6,9.1c1.3-2.3,5.7-10.8,2.8-17.1 C536.3-2,535.2,19,530.8,24.2c0.8-7.3,10.3-22.9-1.9-24.2c-12.6,6.5-8.9,40.8-21.2,50.7c-5.3,2.4-11.9-11.6-18.4-14 c-25.5,0.3,15.8,26,14.2,38.4c-1.8,31.4-26,57.1-45.7,85.1c-9.5,25-26.2,41.2-48,55.9c-2.1,10.1,21.6,77.6,14,48.1 c37.9-48.7,78.7-94.2,94.7-155.7c3.9,6.3,4.8-10.6,7.7-12.8c6-9.6,16-20.3,18.4-32.8C544.6,47.8,567.4,41.7,564.4,27.6z",
  "M120.6,187.3c-5.7-23.2-23-39.4-36.8-57.9C25.1,59.9,108.4,40.8,75,36.6C45.6,73.5,49.1,17.8,38.1,0.8 c-16.3-4.7-4.9,17.4-5.4,23.4c-3.2-6.6-5.6-25.8-15.9-20.9c-4.6,6,2.8,14.3,4.8,20.4C19.7,23.3,15.6,13,13,11.3 c-16.5,0.1-1.4,17.4,3.2,23.6c-25.5-26.5-17,6.5-0.4,17.8c4.6,13.7,10.2,29.9,20.5,40C51,117.5,59.8,147.2,75.6,178 c15.9,33.1,51.4,55,63.8,90.7C164.3,237,153.6,206.3,120.6,187.3z",
  "M258.5,247.9c-4,0.7-4,6.8,2.7,26.1c21.4,29.8,52.5,1,48.4-27.7c5.2-7.3,10-17.2,14.5-30.4c6.2-5.9,13.8-26.2,4.1-19.6 c-1.6-2.1-2.6-4.4-3.2-7c0.5,0.5,1.1,1,1.7,1.4C351.8,85.3,216,119.3,237.8,175c-1.4,6.3-1.7,13.1-1.1,20.1 c-0.2-0.2-0.4-0.4-0.6-0.6c-6.5,2.4-0.9,13.6,4.5,20.2C244.6,227.3,250.9,239.1,258.5,247.9z",
  "M140.5,279.3c67.7,53.7,64.1,157,66.7,237.3c190,47.8,136.4-5,164.8-144.2c11.5-98.2,66.5-44.8,30.7-152.4 c-13.6,44.1-16.3,64.8-7.9,6.6c-17.8,22.2-62.5,37.3-75.4,38.2c-19.6,38.5-53.8,40.4-73.8,0.9c-5.7-4.4-46.8-10.5-60-27.7 C146.5,195.9,169.7,260.1,140.5,279.3z",
];

/** The shot's opposing keeper in its freeze frame (teammate:false — her own keeper is
 *  irrelevant), or null: no fabricated fallback position. */
export function findOpponentGoalkeeper(shot: ProtoEvent): FreezeFramePlayer | null {
  return freezeFrameOf(shot).find((f) => !f.teammate && f.position && f.position.name === "Goalkeeper") || null;
}

const scaleX = (v: number) => GOAL_CHART_VB_W / 2 + (v / GOAL_CHART_Y_RANGE) * (GOAL_CHART_VB_W / 2);
const scaleZ = (v: number) => GOAL_CHART_VB_H - (v / GOAL_CHART_Z_RANGE) * GOAL_CHART_VB_H;

export interface ShotPlacement {
  postL: number;
  postR: number;
  bar: number;
  /** The keeper silhouette box, at this shot's real keeper lateral offset. */
  keeper: { x: number; y: number; width: number; height: number } | null;
  dot: { cx: number; cy: number; fill: string };
}

/** The goal-mouth chart, or null without an end_location. A 2-element end_location (no
 *  height — Blocked/Wayward only) is ground level, not hidden. */
export function shotPlacement(shot: ProtoEvent): ShotPlacement | null {
  const end = shotOf(shot).end_location;
  if (!end) return null;
  const yOff = clamp(end[1]! - GOAL_CENTER_Y, -GOAL_CHART_Y_RANGE, GOAL_CHART_Y_RANGE);
  const z = Math.max(0, typeof end[2] === "number" ? end[2] : 0);
  const gk = findOpponentGoalkeeper(shot);
  let keeper: ShotPlacement["keeper"] = null;
  if (gk) {
    const gkYOff = clamp(gk.location[1]! - GOAL_CENTER_Y, -GOAL_CHART_Y_RANGE, GOAL_CHART_Y_RANGE);
    const height = (GOALKEEPER_HEIGHT_YARDS / GOAL_CHART_Z_RANGE) * GOAL_CHART_VB_H;
    const width = height * GOALKEEPER_SVG_ASPECT;
    keeper = { x: scaleX(gkYOff) - width / 2, y: GOAL_CHART_VB_H - height, width, height };
  }
  return {
    postL: scaleX(-GOAL_HALF_WIDTH),
    postR: scaleX(GOAL_HALF_WIDTH),
    bar: scaleZ(GOAL_HEIGHT),
    keeper,
    dot: { cx: scaleX(yOff), cy: scaleZ(z), fill: shotColor(shot) },
  };
}

// --- Shot Details panel --------------------------------------------------------------------------

/** Base → execution uplift → Final, plus three independent keeper reads, on a fixed 0–1
 *  xG scale (a stable domain beats per-row rescaling for a bounded metric). A missing value
 *  (e.g. Save Difficulty off target) is an honest "—" row. */
export function xgChartRows(shot: ProtoEvent): DivergingRow[] {
  const s = shotOf(shot);
  const f3 = (v: number) => v.toFixed(3);
  return divergingBarRows(
    [
      { label: "Base", value: s.statsbomb_xg, format: f3 },
      { label: "Exec ±", value: s.shot_execution_xg_uplift },
      { label: "Final", value: s.shot_execution_xg, isTotal: true, format: f3 },
      { label: "Save Difficulty", value: s.gk_save_difficulty_xg, format: f3 },
      { label: "GK Positioning", value: s.gk_positioning_xg_suppression, format: f3 },
      { label: "GK Shot-Stopping", value: s.gk_shot_stopping_xg_suppression, format: f3 },
    ],
    1,
  );
}

export interface ShotDetails {
  opposition: string;
  date: string;
  resultText: string;
  resultClass: string;
  time: string;
  outcome: string;
  outcomePillClass: string;
  bodyPart: string;
  type: string;
  technique: string;
  position: string;
  playPattern: string;
  /** Deflected / Under Pressure / Assisted (Assisted is a flag, never "by X" — the key
   *  pass belongs to a teammate's events, which this snapshot doesn't carry). */
  tags: string[];
}

export function shotDetails(ctx: EventContext, shot: ProtoEvent): ShotDetails {
  const raw = sbAttrs(shot);
  const info = shotOf(shot);
  return {
    ...eventMatchRows(ctx, shot),
    time: eventTimeLabel(shot),
    outcome: info.outcome.name,
    outcomePillClass: shotOutcomePillClass(shot),
    bodyPart: info.body_part.name,
    type: info.type.name,
    technique: info.technique ? info.technique.name : "—",
    position: raw.position ? raw.position.name : "—",
    playPattern: raw.play_pattern ? raw.play_pattern.name : "—",
    tags: [info.deflected ? "Deflected" : "", raw.under_pressure ? "Under Pressure" : "", info.key_pass_id ? "Assisted" : ""].filter(Boolean),
  };
}

// --- Stats bar + table ---------------------------------------------------------------------------

export interface ShotStats {
  shown: string;
  npXg: string;
  xgPerShot: string;
  goals: number;
  penalties: string;
}

/** The fixed stats row: shown count ("N of TOTAL" once narrowed), non-penalty xG + xG per
 *  non-penalty shot, goals, penalty goals / penalties. */
export function shotStats(shots: ProtoEvent[], allShots: ProtoEvent[]): ShotStats {
  const nonPen = shots.filter((s) => shotOf(s).type.name !== "Penalty");
  const npXg = nonPen.reduce((sum, s) => sum + (shotOf(s).statsbomb_xg || 0), 0);
  const xgPerShot = nonPen.length ? npXg / nonPen.length : 0;
  const goals = shots.filter((s) => shotOf(s).outcome.name === "Goal").length;
  const penalties = shots.filter((s) => shotOf(s).type.name === "Penalty");
  const penaltyGoals = penalties.filter((s) => shotOf(s).outcome.name === "Goal").length;
  return {
    shown:
      allShots.length !== shots.length
        ? `${shots.length.toLocaleString()} of ${allShots.length.toLocaleString()}`
        : `${shots.length.toLocaleString()}`,
    npXg: npXg.toFixed(2),
    xgPerShot: xgPerShot.toFixed(2),
    goals,
    penalties: `${penaltyGoals} / ${penalties.length}`,
  };
}

export interface ShotTableRow {
  key: string;
  /** Opponent (H/A), or the date where matchResults has no fixture. */
  opposition: string;
  minute: number;
  /** As football shows it ("87'", "90+3'"). */
  clock: string;
  outcome: string;
  xg: string;
}

/** Newest first — the legacy comparator verbatim (not a stable-sort rewrite, so ties land
 *  exactly where they did). */
export function shotTableRows(ctx: EventContext, shots: ProtoEvent[]): ShotTableRow[] {
  return shots
    .slice()
    .sort((a, b) => (a.match_date < b.match_date ? 1 : -1))
    .map((s) => ({
      key: s.id,
      opposition: oppositionLabel(ctx, s) || formatMatchDate(s.match_date),
      minute: s.minute,
      clock: clockMinute(s.minute, s.period),
      outcome: shotOf(s).outcome.name,
      xg: (shotOf(s).statsbomb_xg || 0).toFixed(2),
    }));
}

// --- the xG story (?live=1, Ben 2026-10-18: track 1 — the season behind the totals) ---------------

/** One match in the story: her non-penalty goals and xG that game, and the running totals. */
export interface XgStoryPoint {
  matchId: number;
  date: string;
  /** "Leeds United (H)", or the date where the fixture isn't known. */
  label: string;
  goals: number;
  xg: number;
  cumGoals: number;
  cumXg: number;
}

/** Her season match by match (the matches she shot in, oldest first): non-penalty goals and xG,
 *  each game and running — the Shot Map's "running hot / cold" line. Penalties are left out of
 *  both, so the two lines compare like with like. Pure. */
export function xgStory(ctx: EventContext, shots: readonly ProtoEvent[]): XgStoryPoint[] {
  const byMatch = new Map<number, ProtoEvent[]>();
  for (const s of shots) {
    if (shotOf(s).type.name === "Penalty") continue;
    byMatch.set(s.match_id, [...(byMatch.get(s.match_id) ?? []), s]);
  }
  const matches = [...byMatch.values()].sort((a, b) =>
    a[0]!.match_date < b[0]!.match_date ? -1 : a[0]!.match_date > b[0]!.match_date ? 1 : a[0]!.match_id - b[0]!.match_id,
  );
  let cumGoals = 0;
  let cumXg = 0;
  return matches.map((ms) => {
    const goals = ms.filter((s) => shotOf(s).outcome.name === "Goal").length;
    const xg = ms.reduce((t, s) => t + (shotOf(s).statsbomb_xg || 0), 0);
    cumGoals += goals;
    cumXg += xg;
    const first = ms[0]!;
    return {
      matchId: first.match_id,
      date: first.match_date,
      label: oppositionLabel(ctx, first) || formatMatchDate(first.match_date),
      goals,
      xg,
      cumGoals,
      cumXg,
    };
  });
}
